import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";
import { SeoAccordion } from "@/components/seo-accordion";
import { MARIAGE_SECTIONS } from "@/data/seo-blocks";
import heroImg from "@/assets/dj-mariage-hero.jpg";
import heroVideo from "@/assets/showreel-dance.mp4.asset.json";
import heroVideoPoster from "@/assets/showreel-dance-poster.webp.asset.json";
import portraitImg from "@/assets/dj-mariage-portrait.jpg";
import receptionImg from "@/assets/dj-mariage-reception.jpg";
import danceImg from "@/assets/dj-mariage-dance.jpg";
import equipmentImg from "@/assets/dj-mariage-equipment.jpg";
import ceremonyImg from "@/assets/dj-mariage-ceremony.jpg";
import sparksImg from "@/assets/dj-mariage-sparks.jpg";
import { MARIAGE_PHOTO } from "@/lib/responsive-images";
import { useReveal } from "@/hooks/use-reveal";
import { TldrBox } from "@/components/tldr-box";
import { DevisWizard } from "@/components/devis-wizard";
import { CITIES, CITY_SLUGS } from "@/data/cities";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { BarParfumSection } from "@/components/bar-parfum-section";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
export const Route = createFileRoute("/dj-mariage/")({
  head: () => ({
    meta: [
      { title: "DJ mariage Vaucluse dès 1490 € · 200+ mariages | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("DJ mariage à Avignon, Cavaillon et dans tout le Vaucluse dès 1 490 € : sono, éclairage LED, animation sur mesure, photobooth en option. note Google 5,0. Devis en 24 h.")
      },
      { property: "og:title", content: "DJ mariage Vaucluse dès 1490 € · 200+ mariages | DJ Val" },
      {
        property: "og:description",
        content:
          metaDesc("DJ mariage en Provence dès 1 490 € : sono, éclairage LED et animation sur mesure. 20 ans d'expérience, 41 avis Google. Devis gratuit en 24 h.")
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://dj-val.fr/dj-mariage" },
      { property: "og:image", content: `https://dj-val.fr${heroVideoPoster.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://dj-val.fr${heroVideoPoster.url}` },
    ],
    links: [
      { rel: "canonical", href: "https://dj-val.fr/dj-mariage" },
      { rel: "preload", as: "image", href: heroVideoPoster.url, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "DJ Mariage Provence & Vaucluse",
          provider: { ...LOCAL_BUSINESS_SCHEMA, areaServed: [
              "Avignon", "Cavaillon", "Aix-en-Provence", "Arles", "Nîmes",
              "Orange", "Carpentras", "Saint-Rémy-de-Provence",
              "Salon-de-Provence", "Pertuis", "Apt", "Uzès", "Luberon", "Alpilles",
            ] },
          serviceType: "DJ Mariage, sonorisation, éclairage LED, location de matériel",
          areaServed: ["Cavaillon", "Avignon", "Vaucluse"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Quelle sonorisation prévoyez-vous pour un mariage ou un événement privé ?", acceptedAnswer: { "@type": "Answer", text: "DJ Val installe une sonorisation professionnelle Pioneer et RCF dimensionnée selon la taille de la salle et le nombre d'invités (30 à 400 personnes). Enceintes de façade, caissons de basses, micros HF Shure pour les discours et la cérémonie, doublés en secours." } },
            { "@type": "Question", name: "L'éclairage LED est-il inclus dans la prestation mariage ?", acceptedAnswer: { "@type": "Answer", text: "Oui. Kit d'éclairage LED professionnel : mise en lumière indirecte de la salle, lyres LED pour la piste de danse, machine à fumée légère. Mêmes options pour anniversaire, EVJF/EVG ou soirée d'entreprise." } },
            { "@type": "Question", name: "Sonorisez-vous la cérémonie laïque ou religieuse ?", acceptedAnswer: { "@type": "Answer", text: "Oui, sonorisation complète de votre cérémonie avec micros HF, retours pour les officiants et diffusion musicale haute fidélité, en intérieur comme en extérieur." } },
            { "@type": "Question", name: "Peut-on louer uniquement le matériel sans DJ ?", acceptedAnswer: { "@type": "Answer", text: "Oui. Location de sonorisation et d'éclairage pour mariage, anniversaire, soirée privée ou événement d'entreprise. Livraison, installation et reprise possibles dans toute la Provence." } },
            { "@type": "Question", name: "Animez-vous aussi les anniversaires et soirées privées ?", acceptedAnswer: { "@type": "Answer", text: "Oui, DJ Val anime anniversaires, EVJF/EVG, baptêmes, soirées d'entreprise, cocktails et événements privés." } },
            { "@type": "Question", name: "Quelle est votre zone d'intervention en Provence ?", acceptedAnswer: { "@type": "Answer", text: "Rayon d'environ 100 km autour de Cavaillon : Avignon, Aix-en-Provence, Arles, Nîmes, Orange, Uzès, Salon-de-Provence, Carpentras, Apt, Pertuis, Luberon, Alpilles et Gard rhodanien." } },
            { "@type": "Question", name: "Combien de temps à l'avance faut-il réserver ?", acceptedAnswer: { "@type": "Answer", text: "Entre 6 et 12 mois pour un mariage en haute saison. 2 à 3 mois suffisent pour un anniversaire ou une soirée privée." } },
          ],
        }),
      },
    ],
  }),
  component: DjMariagePage,
});

const cities = CITY_SLUGS.map((slug) => ({ slug, name: CITIES[slug].name, preposition: CITIES[slug].preposition }));

const services = [
  "La mise en lumière de votre lieu de réception (éclairage indirect de la couleur de votre choix, guirlandes lumineuses)",
  "La sonorisation de votre cérémonie laïque ou religieuse",
  "La sonorisation de votre cocktail / vin d'honneur",
  "La vidéoprojection (vidéoprojecteur et écran) pour diaporamas et films",
  "Un feu d'artifice intérieur avec étincelles froides et jets de scène extérieurs",
  "Une machine à bulles pour sortie d'église/mairie, entrée de cérémonie, ouverture de bal",
  "Une ouverture de bal magique grâce à notre fumée lourde",
];

const reviews = [
  {
    name: "Emilie Blanc",
    text: "J'étais invitée à un mariage animé par DJ Val et franchement j'ai adoré la soirée. Tout était super bien organisé du début à la fin, on sent que c'est quelqu'un qui maîtrise vraiment son métier. La musique était top, l'ambiance était géniale, la piste de danse ne s'est jamais vidée. Je recommande DJ Val sans hésiter.",
  },
  {
    name: "Béatrice Iacopini",
    text: "Nous avons fait appel à DJ Val pour notre mariage à Avignon, et nous ne pouvions pas rêver mieux. Il a pris le temps de comprendre nos goûts musicaux. Le jour J, tout a été parfaitement géré : sonorisation de la cérémonie, vin d'honneur et soirée dansante exceptionnelle. Une prestation élégante, une vraie tranquillité d'esprit pour les mariés.",
  },
  {
    name: "Mélanie Perez",
    text: "Location de matériel sonorisation et lumières pour un mariage. Superbe qualité et service au top. Diversité de matériel et réponse rapide, je recommande les yeux fermés !",
  },
];

const steps = [
  {
    n: "01",
    title: "Premier échange",
    text: "Nous discutons de votre mariage, du lieu, du nombre d'invités, de vos goûts musicaux et de l'ambiance souhaitée. Devis personnalisé sous 24h.",
  },
  {
    n: "02",
    title: "Préparation sur mesure",
    text: "Construction de la playlist, repérage technique du lieu, validation des moments clés : cérémonie, cocktail, entrée des mariés, ouverture de bal.",
  },
  {
    n: "03",
    title: "Installation discrète",
    text: "Arrivée 3 heures avant le début. Mise en place du matériel, des éclairages LED et tests sonorisation pendant que vos invités profitent du vin d'honneur.",
  },
  {
    n: "04",
    title: "Animation jusqu'au bout de la nuit",
    text: "Lecture en temps réel de votre piste de danse, transitions parfaites, gestion micro pour les discours et coordination avec photographe & traiteur.",
  },
];

const faqs = [
  {
    q: "Quelle sonorisation prévoyez-vous pour un mariage ou un événement privé ?",
    a: "DJ Val installe une sonorisation professionnelle Pioneer et RCF dimensionnée selon la taille de la salle et le nombre d'invités (30 à 400 personnes). Enceintes de façade, caissons de basses, micros HF Shure pour les discours et la cérémonie : un système complet, doublé en secours, pour un mariage ou une soirée privée sans coupure.",
  },
  {
    q: "L'éclairage LED est-il inclus dans la prestation mariage ?",
    a: "Oui. Chaque prestation DJ mariage inclut un kit d'éclairage LED professionnel : mise en lumière indirecte de la salle aux couleurs de votre thème, lyres LED pour la piste de danse, machine à fumée légère pour l'ouverture de bal. Mêmes options disponibles pour un anniversaire, EVJF/EVG ou soirée d'entreprise.",
  },
  {
    q: "Sonorisez-vous la cérémonie laïque ou religieuse ?",
    a: "Oui, DJ Val propose la sonorisation complète de votre cérémonie (laïque, religieuse ou civile) avec micros HF discrets, retours pour les officiants et diffusion musicale haute fidélité, en intérieur comme en extérieur, vin d'honneur compris.",
  },
  {
    q: "Peut-on louer uniquement le matériel sans DJ ?",
    a: "Oui. En plus de l'animation, nous proposons la location de matériel de sonorisation et d'éclairage pour mariage, anniversaire, soirée privée ou événement d'entreprise : enceintes amplifiées, table de mixage, micros HF, machines à fumée, lyres LED. Livraison, installation et reprise possibles dans toute la Provence.",
  },
  {
    q: "Animez-vous aussi les anniversaires et soirées privées ?",
    a: "Bien sûr. Au-delà du mariage, DJ Val anime anniversaires, EVJF/EVG, baptêmes, soirées d'entreprise, cocktails et événements privés. Même exigence musicale, même qualité de matériel, et une playlist construite avec vous selon vos invités.",
  },
  {
    q: "Quelle est votre zone d'intervention en Provence ?",
    a: "DJ Val se déplace dans un rayon d'environ 100 km autour de Cavaillon : Avignon, Aix-en-Provence, Arles, Nîmes, Orange, Uzès, Salon-de-Provence, Carpentras, Apt, Pertuis, ainsi que tout le Luberon, les Alpilles et le Gard rhodanien. Devis sous 24 h pour mariages et événements privés.",
  },
  {
    q: "Combien de temps à l'avance faut-il réserver ?",
    a: "Idéalement entre 6 et 12 mois avant la date pour un mariage. Les samedis de mai à septembre dans le Vaucluse, Avignon ou Aix sont souvent réservés un an à l'avance. Pour un anniversaire ou une soirée privée, 2 à 3 mois suffisent généralement.",
  },
];

const stats = [
  { value: "200", suffix: "+", label: "Mariages animés", sub: "Provence & Vaucluse" },
  { value: "500", suffix: "+", label: "Événements privés", sub: "Anniversaires, EVJF, entreprises" },
  { value: "1000", suffix: "+", label: "Locations de matériel", sub: "Sono & éclairage LED" },
];

function Stars({ size = "text-xs" }: { size?: string }) {
  return (
    <span className={`tracking-[0.4em] text-foreground ${size}`} aria-label="5 étoiles sur 5">
      ★★★★★
    </span>
  );
}

function SectionLabel({ n, label }: { n: string; label: string }) {
  return (
    <div className="mx-auto mb-8 flex items-center justify-center gap-4 sr" data-reveal>
      <span className="font-serif-display text-foreground italic text-lg">{n}</span>
      <span className="h-px w-10 bg-foreground/10" />
      <span className="eyebrow !tracking-[0.4em]">{label}</span>
      <span className="h-px w-10 bg-foreground/10" />
      <span className="font-serif-display text-foreground italic text-lg">{n}</span>
    </div>
  );
}

function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Détection prefers-reduced-motion → fallback poster, pas d'autoplay
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Autoplay contrôlé via IntersectionObserver (avec fallback si bloqué)
  useEffect(() => {
    const v = videoRef.current;
    if (!v || reducedMotion) return;
    if (typeof IntersectionObserver === "undefined") {
      v.play().then(() => setPlaying(true)).catch(() => setFailed(true));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            v.play().then(() => setPlaying(true)).catch(() => setFailed(true));
          } else {
            v.pause();
            setPlaying(false);
          }
        }
      },
      { threshold: 0.2 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [reducedMotion]);

  // Loop fluide : on évite le saut au début en bouclant manuellement avec une légère anticipation
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration || isNaN(v.duration)) return;
    // Bascule douce 80ms avant la fin pour éviter le flash
    if (v.duration - v.currentTime < 0.08) {
      v.currentTime = 0;
    }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  // Fallback poster (reduced motion ou autoplay refusé)
  if (reducedMotion || failed) {
    return (
      <img
        src={heroVideoPoster.url}
        alt="Scène nuit d'un mariage animé par DJ Val en Provence — dancefloor éclairé"
        className="h-full w-full object-cover"
      />
    );
  }

  return (
    <>
      <video
        ref={videoRef}
        src={heroVideo.url}
        poster={heroVideoPoster.url}
        muted
        loop
        playsInline
        preload="metadata"
        onTimeUpdate={onTimeUpdate}
        onError={() => setFailed(true)}
        aria-label="Vidéo : scène nuit d'un mariage animé par DJ Val en Provence"
        className="h-full w-full object-cover"
      />
      {/* Contrôles accessibilité — discrets, en bas à droite */}
      <div className="absolute bottom-5 right-5 z-20 flex gap-2">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Mettre la vidéo en pause" : "Lire la vidéo"}
          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/55 hover:bg-[var(--gold)] hover:text-[var(--ink)] text-white border border-white/30 backdrop-blur-sm transition-colors"
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-[2px]" />}
        </button>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Activer le son de la vidéo" : "Couper le son de la vidéo"}
          aria-pressed={!muted}
          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/55 hover:bg-[var(--gold)] hover:text-[var(--ink)] text-white border border-white/30 backdrop-blur-sm transition-colors"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </>
  );
}

function DjMariagePage() {
  useReveal();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="bg-background text-muted-foreground selection:bg-[var(--gold)] selection:text-[var(--ink)] overflow-x-hidden">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[100vh] flex flex-col items-center justify-center text-center px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <HeroVideo />
          {/* Léger vignettage haut/bas uniquement — la vidéo respire */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0) 22%, rgba(10,10,10,0) 65%, rgba(10,10,10,0.85) 100%)",
            }}
            aria-hidden="true"
          />
          {/* Halo radial centré pour booster la lisibilité du titre sans assombrir tout l'écran */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 65% 45% at 50% 50%, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.25) 45%, transparent 75%)",
            }}
            aria-hidden="true"
          />
        </div>

        <div className="relative z-10 reveal space-y-7 max-w-2xl [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]">
          <p className="eyebrow text-white/85">DJ Val · Provence · Vaucluse</p>
          <h1 className="font-serif-display text-white text-4xl sm:text-5xl md:text-6xl leading-[1.02]">
            <span className="italic font-light block">DJ Mariage</span>
            <span className="block font-light mt-2 text-white">en Provence &amp; Vaucluse</span>
          </h1>
          <span className="gold-rule mx-auto !w-12 my-3" />
          <p className="text-[11px] sm:text-xs tracking-[0.3em] text-white/85 uppercase leading-loose">
            Plus de 20 ans d'expérience · Avignon · Cavaillon · Aix · Arles · Nîmes
          </p>

          <div className="pt-6 flex flex-col sm:flex-row gap-3 items-center justify-center [text-shadow:none]">
            <Link
              to="/contactez-nous"
              search={{ sujet: "DJ Mariage en Provence & Vaucluse" }}
              className="bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all duration-500 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.7)]"
            >
              Devis gratuit
            </Link>

            <a
              href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "dj_mariage" })}
              className="text-white border border-white/40 bg-black/30 px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] backdrop-blur-sm hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
            >
              07 67 90 86 67
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
          <span className="text-[9px] tracking-[0.4em] text-muted-foreground uppercase">Découvrir</span>
          <span className="h-12 w-px bg-gradient-to-b from-[var(--gold)] to-transparent" />
        </div>
      </section>
      <BarParfumSection />
      <ReviewsProof />

      {/* ============ MARQUEE LOCATIONS ============ */}
      <div className="border-y border-foreground/10 bg-card py-6 overflow-hidden">
        <div className="marquee-track whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 pr-12">
              {cities.map((c) => (
                <span key={`${k}-${c.slug}`} className="flex items-center gap-12">
                  <span className="font-serif-display italic text-foreground/90 text-2xl sm:text-3xl">
                    {c.name}
                  </span>
                  <span className="text-foreground/90">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ============ TLDR / AIO ============ */}
      <div className="px-6 pt-12">
        <TldrBox
          summary="DJ Val est DJ mariage professionnel basé à Cavaillon, intervient dans toute la Provence (Vaucluse, Bouches-du-Rhône, Gard) depuis 20 ans. Animation sur mesure, sonorisation Pioneer / RCF et éclairage LED inclus, devis transparent sous 24 h."
          items={[
            { label: "Tarif moyen mariage", value: "À partir de 1 490 € (formule Signature 8 h)" },
            { label: "Zone d'intervention", value: "Vaucluse, Bouches-du-Rhône, Gard (≈ 100 km)" },
            { label: "Délai de réservation", value: "6 à 12 mois avant la date conseillé" },
            { label: "Inclus", value: "Sono, éclairage, micros HF, RDV de préparation" },
          ]}
        />
      </div>

      {/* ============ INTRO BLOCK ============ */}
      <section className="py-24 sm:py-32 px-8 text-center max-w-3xl mx-auto">
        <SectionLabel n="01" label="Introduction" />
        <h2
          className="font-serif-display text-3xl sm:text-5xl text-foreground leading-tight sr"
          data-reveal
        >
          DJ Mariage &amp; Location de Matériel
          <span className="block italic text-foreground mt-3">dans le Vaucluse et ses Environs</span>
        </h2>
        <span className="gold-rule mx-auto !w-12 mt-8 mb-8 sr d-1" data-reveal />
        <p className="text-sm sm:text-base text-muted-foreground leading-loose font-light sr d-2" data-reveal>
          Vous êtes à la recherche d'un{" "}
          <span className="text-foreground font-medium">DJ mariage</span>, DJ anniversaire, ou d'un DJ
          pour une soirée privée dans le Vaucluse ? Chez{" "}
          <span className="text-foreground font-medium">DJ Val</span>, nous proposons des services
          d'animation sur mesure ainsi que la{" "}
          <Link to="/location-materiel" className="text-foreground underline decoration-[var(--gold)] underline-offset-4 hover:text-[var(--gold)] transition-colors">
            location de matériel
          </Link>{" "}
          de{" "}
          <Link to="/sono-lumiere" className="text-foreground underline decoration-[var(--gold)] underline-offset-4 hover:text-[var(--gold)] transition-colors">
            sonorisation et d'éclairage LED
          </Link>{" "}
          pour tous vos événements. Que vous soyez à Avignon, Cavaillon, ou dans les villes
          voisines telles qu'Orange, Carpentras, L'Isle-sur-la-Sorgue, Saint-Rémy-de-Provence,
          Arles, Nîmes, Salon-de-Provence, ou encore Pertuis, nous intervenons dans un rayon de
          150 kilomètres pour rendre vos moments inoubliables.
        </p>
        <p className="mt-6 text-sm text-muted-foreground leading-loose font-light sr d-3" data-reveal>
          Envie d'aller plus loin ? Découvrez notre{" "}
          <Link to="/dj-mariage/pack-sono-lumiere" className="text-foreground underline decoration-[var(--gold)] underline-offset-4 hover:text-[var(--gold)] transition-colors">
            pack sono &amp; lumière mariage
          </Link>{" "}
          ou ajoutez un{" "}
          <Link to="/location-materiel/$slug" params={{ slug: "photobooth" }} className="text-foreground underline decoration-[var(--gold)] underline-offset-4 hover:text-[var(--gold)] transition-colors">
            photobooth
          </Link>{" "}
          pour immortaliser la soirée avec vos invités.
        </p>

      </section>

      {/* ============ PACK DJ + PHOTOBOOTH ============ */}
      <section className="px-6 pb-8">
        <div className="max-w-4xl mx-auto border border-[var(--gold)]/40 bg-[var(--gold)]/[0.06] p-8 sm:p-10 text-center">
          <p className="eyebrow text-foreground">Offre combinée</p>
          <h2 className="font-serif-display text-2xl sm:text-3xl text-foreground mt-3">
            Ajoutez le <span className="italic">photobooth</span> à votre mariage
          </h2>
          <p className="mt-4 text-sm text-muted-foreground font-light leading-relaxed max-w-2xl mx-auto">
            Borne tactile et photos illimitées à partir de 190 €, ou 390 € avec les tirages 10×15 illimités.
            Réservé avec une prestation DJ, la livraison, l'installation et le démontage sont offerts, et
            j'anime la borne pendant la soirée — un seul prestataire, un seul interlocuteur.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/location-photobooth-avignon"
              className="bg-[var(--gold)] text-foreground px-8 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Voir le photobooth
            </Link>
            <Link
              to="/contactez-nous"
              className="border border-foreground/20 text-foreground px-8 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] transition-colors"
            >
              Devis DJ + photobooth
            </Link>
          </div>
        </div>
      </section>

      {/* ============ CHIFFRES CLÉS ============ */}
      <section className="relative border-y border-foreground/10 bg-card overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, var(--gold) 0, transparent 40%), radial-gradient(circle at 80% 80%, var(--gold) 0, transparent 40%)" }} aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 sm:py-28">
          <div className="text-center mb-14 sm:mb-20">
            <SectionLabel n="02" label="Chiffres clés" />
            <h2 className="font-serif-display text-3xl sm:text-5xl text-foreground leading-tight sr" data-reveal>
              20 ans d'expérience<br />
              <span className="italic text-foreground">au service de vos événements</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-8" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-foreground/10 border border-foreground/10">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`bg-background text-center py-14 sm:py-16 px-6 sr d-${i + 1}`}
                data-reveal
              >
                <p className="font-serif-display italic gold-text-shine text-4xl sm:text-5xl md:text-6xl leading-none">
                  {s.value}
                  <span className="text-foreground not-italic font-light">{s.suffix}</span>
                </p>
                <span className="block h-px w-10 bg-[var(--gold)] mx-auto my-6" />
                <p className="text-[11px] uppercase tracking-[0.3em] text-foreground font-semibold">
                  {s.label}
                </p>
                <p className="mt-2 text-xs text-muted-foreground font-light">
                  {s.sub}
                </p>
              </div>
            ))}
          </div>
          <p className="sr-only">
            DJ Val en chiffres : plus de 200 mariages animés en Provence et Vaucluse, plus de 500 événements privés (anniversaires, EVJF, soirées d'entreprise) et plus de 1000 locations de matériel de sonorisation et d'éclairage LED.
          </p>
        </div>
      </section>



      {/* ============ ALTERNATING BLOCK 1 — Image LEFT ============ */}
      <section className="py-24 sm:py-36 px-6 sm:px-10 max-w-6xl mx-auto">
        <div className="grid gap-16 lg:gap-24 lg:grid-cols-2 lg:items-center">
          <div className="relative sr-left" data-reveal>
            <div className="relative overflow-hidden">
              <img
                src={portraitImg}
                alt="DJ Val mariage Avignon Vaucluse — animation musicale haut de gamme"
                className="w-full aspect-[3/4] object-cover kenburns-img"
                loading="lazy"
                width={1080}
                height={1440}
              />
              <div className="pointer-events-none absolute inset-2 border border-foreground/10" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[var(--gold)] text-[var(--ink)] px-6 py-4 hidden sm:block">
              <p className="font-serif-display italic text-2xl leading-none">20+ ans</p>
              <p className="text-[9px] uppercase tracking-[0.3em] mt-1 font-semibold">
                d'expérience
              </p>
            </div>
          </div>
          <div className="space-y-6 sr-right" data-reveal>
            <p className="eyebrow">02 — Notre approche</p>
            <h2 className="font-serif-display text-3xl sm:text-5xl text-foreground leading-[1.05]">
              Une <span className="italic text-foreground">programmation</span> entièrement sur mesure
            </h2>
            <span className="gold-rule !w-10" />
            <p className="text-sm text-muted-foreground leading-loose font-light">
              Nous proposons une programmation musicale sur mesure adaptée à vos goûts et à ceux
              de vos invités. Grâce à notre sonorisation professionnelle et à un éclairage LED
              moderne, nous assurons une ambiance parfaite pour votre{" "}
              <span className="text-foreground">mariage en Provence</span>.
            </p>
            <p className="text-sm text-muted-foreground leading-loose font-light">
              Chaque DJ mariage commence par une rencontre. Nous prenons le temps de comprendre
              votre histoire, vos morceaux essentiels et les ambiances qui vous ressemblent — du
              cocktail élégant à la piste de danse incandescente.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/contactez-nous"
                search={{ sujet: "DJ Mariage en Provence & Vaucluse" }}
                className="border border-[var(--gold)] text-foreground px-8 py-3.5 text-[10px] font-semibold uppercase tracking-[0.25em] hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors"
              >
                Réserver votre DJ
              </Link>

            </div>
          </div>
        </div>
      </section>

      {/* ============ PARALLAX QUOTE ============ */}
      <section
        className="relative py-24 sm:py-48 px-6 text-center parallax-bg"
        style={{ backgroundImage: `url(${receptionImg})` }}
      >
        <div className="absolute inset-0 bg-background/75" aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto sr-zoom" data-reveal>
          <span className="font-serif-display italic text-foreground text-7xl leading-none block mb-6">
            ”
          </span>
          <p className="font-serif-display italic text-foreground text-2xl sm:text-4xl leading-snug">
            Un mariage réussi, c'est une piste de danse qui ne se vide jamais —
            <span className="text-foreground"> et des invités qui en parlent encore des années plus tard.</span>
          </p>
          <span className="gold-rule mx-auto !w-16 mt-10 mb-6" />
          <p className="eyebrow !text-[10px]">DJ Val · Animation mariage Provence</p>
        </div>
      </section>

      {/* ============ ALTERNATING BLOCK 2 — Image RIGHT ============ */}
      <section className="py-24 sm:py-36 px-6 sm:px-10 max-w-6xl mx-auto">
        <div className="grid gap-16 lg:gap-24 lg:grid-cols-2 lg:items-center">
          <div className="space-y-6 order-2 lg:order-1 sr-left" data-reveal>
            <p className="eyebrow">03 — Cérémonie &amp; cocktail</p>
            <h2 className="font-serif-display text-3xl sm:text-5xl text-foreground leading-[1.05]">
              De la <span className="italic text-foreground">cérémonie</span> au dernier slow
            </h2>
            <span className="gold-rule !w-10" />
            <p className="text-sm text-muted-foreground leading-loose font-light">
              DJ Val accompagne chaque temps fort de votre journée : sonorisation de la cérémonie
              laïque ou religieuse, ambiance feutrée pour le vin d'honneur, transition en douceur
              vers le dîner, puis explosion festive jusqu'au bout de la nuit.
            </p>
            <ul className="space-y-3 pt-2">
              {["Micros HF discrets pour les officiants", "Playlist cocktail jazz, lounge ou bossa", "Coordination avec photographe & traiteur", "Animations sur mesure : entrée des mariés, ouverture de bal"].map(
                (item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground font-light">
                    <span className="text-foreground mt-0.5">✦</span>
                    <span>{item}</span>
                  </li>
                ),
              )}
            </ul>
          </div>
          <div className="relative order-1 lg:order-2 sr-right" data-reveal>
            <div className="relative overflow-hidden">
              <img
                src={MARIAGE_PHOTO.src}
                srcSet={MARIAGE_PHOTO.srcSet}
                sizes={MARIAGE_PHOTO.sizes}
                decoding="async"
                alt="Mariage de Janine et Richard à Saint-Rémy-de-Provence — animation DJ Val"
                className="w-full aspect-[4/5] object-cover kenburns-img"
                loading="lazy"
                width={1200}
                height={1500}
              />
              <div className="pointer-events-none absolute inset-2 border border-foreground/10" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ FULL-WIDTH PARALLAX BANNER ============ */}
      <section
        className="relative h-[60vh] parallax-bg flex items-center justify-center"
        style={{ backgroundImage: `url(${danceImg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/50 to-[var(--ink)]/70" />
        <div className="relative text-center px-6 sr-zoom" data-reveal>
          <p className="eyebrow !text-foreground/85">Ouverture de bal</p>
          <h2 className="font-serif-display italic text-foreground text-3xl sm:text-4xl md:text-5xl mt-4">
            La nuit n'a qu'une <span className="text-foreground">étincelle</span> à attraper
          </h2>
        </div>
      </section>

      {/* ============ SERVICES LIST ============ */}
      <section className="py-24 sm:py-32 bg-card px-6">
        <div className="max-w-3xl mx-auto">
          <SectionLabel n="04" label="Prestations complémentaires" />
          <h2
            className="text-center font-serif-display text-3xl sm:text-5xl text-foreground italic mb-14 leading-tight sr"
            data-reveal
          >
            Nous pouvons également<br /> nous occuper de
          </h2>
          <ul className="space-y-px bg-foreground/10">
            {services.map((s, i) => (
              <li
                key={s}
                className={`flex items-start gap-5 bg-card p-6 sm:p-7 hover:bg-[#0b1428] transition-colors sr d-${(i % 5) + 1}`}
                data-reveal
              >
                <span className="font-serif-display italic text-foreground text-sm pt-0.5 min-w-[28px]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[13px] sm:text-sm text-muted-foreground leading-relaxed font-light">
                  {s}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ PROCESS STEPS — alternating zigzag ============ */}
      <section className="py-24 sm:py-32 px-6 max-w-5xl mx-auto">
        <SectionLabel n="05" label="Comment ça se passe" />
        <h2
          className="text-center font-serif-display text-3xl sm:text-5xl text-foreground italic mb-20 leading-tight sr"
          data-reveal
        >
          De la première rencontre<br /> au dernier morceau
        </h2>

        <div className="space-y-16">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className={`grid sm:grid-cols-[120px_1fr] gap-8 items-start ${
                i % 2 === 1 ? "sm:pl-20" : ""
              } sr`}
              data-reveal
            >
              <div className="flex sm:flex-col items-center sm:items-start gap-4">
                <span className="font-serif-display italic text-foreground text-4xl sm:text-5xl md:text-6xl leading-none">
                  {s.n}
                </span>
                <span className="h-px w-12 sm:w-full bg-foreground/10" />
              </div>
              <div className="border-l border-foreground/10 pl-8">
                <h3 className="font-serif-display text-foreground text-2xl sm:text-3xl mb-3">
                  {s.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-loose font-light max-w-xl">
                  {s.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ EQUIPMENT FULL-WIDTH ============ */}
      <section
        className="relative h-[55vh] parallax-bg"
        style={{ backgroundImage: `url(${equipmentImg})` }}
      >
        <div className="absolute inset-0 bg-background/60" />
        <div className="relative h-full flex items-center justify-end px-6 sm:px-16">
          <div className="max-w-md text-right sr-right" data-reveal>
            <p className="eyebrow !text-foreground/85">Matériel professionnel</p>
            <h2 className="font-serif-display text-foreground text-3xl sm:text-5xl mt-4 leading-tight">
              Sonorisation &amp; <span className="italic text-foreground">éclairage LED</span> haut de gamme
            </h2>
            <p className="text-sm text-foreground/85 mt-5 font-light leading-loose">
              Enceintes RCF, table Pioneer, micros Shure HF, lyres LED, machine à fumée lourde,
              étincelles froides. Tout est doublé : zéro risque de coupure le jour J.
            </p>
          </div>
        </div>
      </section>

      {/* ============ MAILLAGE INTERNE — Prestations associées ============ */}
      <section className="py-24 sm:py-32 px-6 bg-card">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <SectionLabel n="06" label="Aller plus loin" />
            <h2 className="font-serif-display text-3xl sm:text-5xl text-foreground leading-tight sr" data-reveal>
              Au-delà du <span className="italic">mariage</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-8" />
            <p className="max-w-2xl mx-auto mt-6 text-sm text-muted-foreground leading-loose font-light sr d-1" data-reveal>
              DJ Val anime également vos événements privés et propose la location de matériel
              de sonorisation et d'éclairage LED dans toute la Provence.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Link
              to="/soirees-evenements"
              className="group relative border border-foreground/10 p-8 sm:p-10 bg-background hover:bg-[#0b1428] transition-colors sr d-1"
              data-reveal
            >
              <p className="eyebrow">DJ Événements privés</p>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-foreground mt-4 leading-snug">
                Anniversaires, EVJF, soirées d'entreprise
              </h3>
              <span className="gold-rule !w-8 my-5" />
              <p className="text-sm text-muted-foreground leading-loose font-light">
                Animation DJ pour vos anniversaires, EVJF/EVG, baptêmes, soirées d'entreprise et
                cocktails. Même exigence que pour un mariage : sonorisation pro, éclairage LED
                et playlist construite avec vous.
              </p>
              <span className="mt-6 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-foreground font-semibold group-hover:gap-5 transition-all">
                Découvrir les événements privés
                <span className="h-px w-6 bg-[var(--gold)]" />
              </span>
            </Link>
            <Link
              to="/location-materiel"
              className="group relative border border-foreground/10 p-8 sm:p-10 bg-background hover:bg-[#0b1428] transition-colors sr d-2"
              data-reveal
            >
              <p className="eyebrow">Location de matériel</p>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-foreground mt-4 leading-snug">
                Sonorisation &amp; éclairage LED en Provence
              </h3>
              <span className="gold-rule !w-8 my-5" />
              <p className="text-sm text-muted-foreground leading-loose font-light">
                Enceintes amplifiées, table de mixage, micros HF, lyres LED, machines à fumée :
                louez le matériel utilisé sur les mariages, avec livraison et installation
                possibles dans toute la Provence.
              </p>
              <span className="mt-6 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-foreground font-semibold group-hover:gap-5 transition-all">
                Voir le matériel disponible
                <span className="h-px w-6 bg-[var(--gold)]" />
              </span>
            </Link>
          </div>
        </div>
      </section>



      {/* ============ TESTIMONIALS ============ */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <SectionLabel n="06" label="Témoignages" />
          <h2
            className="font-serif-display text-3xl sm:text-5xl text-foreground italic mb-6 leading-tight sr"
            data-reveal
          >
            Ce que disent nos clients
          </h2>
          <div className="inline-flex items-center gap-3 border border-foreground/10 px-5 py-2.5 sr d-1" data-reveal>
            <Stars />
            <span className="text-[9px] text-muted-foreground uppercase tracking-[0.25em] font-semibold">
              Excellent · 41 avis Google
            </span>
          </div>

          <div className="mt-16 grid sm:grid-cols-1 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <article
                key={r.name}
                className={`relative border border-foreground/10 p-8 sm:p-10 text-left flex flex-col bg-card/40 hover:bg-card transition-colors sr d-${i + 1}`}
                data-reveal
              >
                <span
                  aria-hidden="true"
                  className="absolute -top-5 left-6 bg-background px-3 font-serif-display italic text-foreground text-4xl leading-none"
                >
                  ”
                </span>
                <Stars size="text-[10px]" />
                <p className="mt-5 flex-1 text-[13px] text-muted-foreground italic leading-loose font-light">
                  {r.text}
                </p>
                <div className="mt-7 flex items-center gap-3">
                  <span className="h-px w-8 bg-[var(--gold)]" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-foreground font-semibold">
                    {r.name}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ACCORDION ============ */}
      <section className="py-24 sm:py-32 bg-card px-6">
        <div className="max-w-3xl mx-auto">
          <SectionLabel n="07" label="Questions fréquentes" />
          <h2
            className="text-center font-serif-display text-3xl sm:text-5xl text-foreground italic mb-14 leading-tight sr"
            data-reveal
          >
            Tout ce que vous devez<br /> savoir avant de réserver
          </h2>

          <div className="border-t border-foreground/10">
            {faqs.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={f.q}
                  className="border-b border-foreground/10 sr"
                  data-reveal
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-6 py-6 sm:py-7 text-left group"
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-baseline gap-5">
                      <span className="font-serif-display italic text-foreground text-sm">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-serif-display text-foreground text-lg sm:text-2xl leading-snug group-hover:text-foreground transition-colors">
                        {f.q}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 h-8 w-8 border border-[var(--gold)] flex items-center justify-center text-foreground transition-transform duration-500 ${
                        isOpen ? "rotate-45" : ""
                      }`}
                      aria-hidden="true"
                    >
                      <span className="text-lg leading-none">+</span>
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-500 ease-out ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100 pb-7"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-sm text-muted-foreground leading-loose font-light pl-10 pr-12 max-w-2xl">
                        {f.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ GEO ZONE BENTO ============ */}
      <section className="py-24 sm:py-32 bg-[var(--ink)] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="mx-auto mb-8 flex items-center justify-center gap-4 sr" data-reveal>
              <span className="font-serif-display text-white italic text-lg">08</span>
              <span className="h-px w-10 bg-white/25" />
              <span className="eyebrow !tracking-[0.4em] text-white/80">Zone d'intervention</span>
              <span className="h-px w-10 bg-white/25" />
              <span className="font-serif-display text-white italic text-lg">08</span>
            </div>
            <h2
              className="font-serif-display text-3xl sm:text-5xl text-white leading-tight sr"
              data-reveal
            >
              Votre <span className="italic text-white">DJ Mariage</span> intervient
              <span className="block text-white">dans toute la Provence</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-8 mb-8" />
            <p
              className="max-w-2xl mx-auto text-sm text-white/80 leading-loose font-light sr d-1"
              data-reveal
            >
              DJ Val intervient dans un rayon d'environ 100 km autour de Cavaillon. Cliquez sur votre
              ville pour demander un devis personnalisé pour votre mariage.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {cities.map((c, i) => (
              <Link
                key={c.slug}
                to="/dj-mariage/$ville"
                params={{ ville: c.slug }}
                className={`group bg-[var(--ink)] p-6 hover:bg-white/10 focus-visible:bg-white/10 transition-all duration-500 sr d-${(i % 5) + 1}`}
                data-reveal
                aria-label={`DJ mariage ${c.preposition || "à"} ${c.name}`}
              >
                <p className="text-[8px] uppercase tracking-[0.3em] text-white/70 group-hover:text-[var(--gold)] mb-2 transition-colors">
                  DJ Mariage
                </p>
                <p className="font-serif-display text-white group-hover:text-white text-lg sm:text-xl leading-tight transition-colors">
                  {c.name}
                </p>
                <span className="block h-px w-6 bg-[var(--gold)] group-hover:w-12 mt-3 transition-all duration-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ DEVIS EN LIGNE — formulaire intégré ============ */}
      <section id="devis-mariage" className="py-24 sm:py-32 px-6 scroll-mt-24">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-14 items-start">
          <div className="sr" data-reveal>
            <p className="eyebrow text-foreground">Votre devis</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
              Recevez votre devis
              <span className="block italic mt-1">sous 24 heures</span>
            </h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-sm text-muted-foreground leading-loose font-light">
              Indiquez simplement votre nom, votre téléphone et la date envisagée : je vous
              rappelle pour affiner l'ambiance, le lieu et le nombre d'invités. Sans engagement.
            </p>
            <ul className="mt-8 space-y-3 text-xs text-muted-foreground font-light">
              <li>Réponse sous 24 h, 7j/7</li>
              <li>Prestations mariage à partir de 1 490 €</li>
              <li>Sono, éclairage LED et cérémonie inclus</li>
            </ul>
            <p className="mt-6 text-xs text-muted-foreground font-light leading-relaxed">
              Besoin de situer votre budget ? Lisez le guide des{" "}
              <Link
                to="/actualites/$slug"
                params={{ slug: "prix-dj-mariage-provence-guide-complet-2026" }}
                className="text-[var(--gold)] underline underline-offset-4"
              >
                tarifs d'un DJ de mariage en Provence
              </Link>
              .
            </p>

          </div>
          <div className="sr d-1" data-reveal>
            <DevisWizard initialSubject="DJ Mariage en Provence & Vaucluse" />
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA — gold panel ============ */}
      <section
        className="relative py-28 sm:py-40 px-8 text-center overflow-hidden parallax-bg"
        style={{ backgroundImage: `url(${sparksImg})` }}
      >
        <div className="absolute inset-0 bg-[var(--gold)]/92" />
        <div className="relative max-w-2xl mx-auto text-[var(--ink)] sr-zoom" data-reveal>
          <p className="text-[10px] uppercase tracking-[0.4em] font-bold opacity-60 mb-8">
            09 — Réservation
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
            Prêt à faire de votre mariage
            <span className="block italic font-light mt-2">un moment inoubliable ?</span>
          </h2>
          <span className="block h-px w-12 bg-background/40 mx-auto my-10" />
          <p className="text-[12px] sm:text-sm uppercase tracking-[0.25em] font-semibold opacity-75">
            Devis gratuit · Disponible 7j/7 · Réponse sous 24h
          </p>

          <div className="mt-12 space-y-4 max-w-sm mx-auto">
            <a
              href="#devis-mariage"
              onClick={() => trackEvent("cta_click", { cta_name: "devis_gratuit", cta_location: "dj_mariage_final" })}
              className="block bg-background text-foreground py-5 text-[11px] font-semibold uppercase tracking-[0.3em] shadow-2xl hover:bg-black transition-colors"
            >
              Demander mon devis gratuit
            </a>

            <a
              href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "dj_mariage" })}
              className="flex items-center justify-center gap-3 text-[15px] font-semibold py-4 border-t border-[var(--ink)]/15 mt-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.24.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z" />
              </svg>
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>

      <SeoAccordion
        eyebrow="DJ Mariage Provence"
        title={<>Vos questions sur un <span className="italic">DJ mariage</span></>}
        intro="Tarifs, délais, matériel, lieux : les réponses détaillées que cherchent les futurs mariés en Provence."
        sections={MARIAGE_SECTIONS}
      />
    </div>
  );
}
